import { compareApplications } from '@/BlockEngineComparer/comparerUtils/compareApplications';
import { ComparerContextProvider } from '@/context/ComparerContextProvider';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { ComparerSummaryID, ComparerResults as TypeComparerResults } from '@/types/comparer';
import { migrateApplication } from '@/util/migration/migration';
import { Stack } from '@mui/system';
import { useEffect, useState, type FC } from 'react';
import ComparerResults from './ComparerResults';
import ComparerSummary from './ComparerSummary';

type ComparerResultsContainerProps = {
  savedAppA: Application;
  savedAppB: Application;
  savedLabelA: string;
  savedLabelB: string;
};

const ComparerResultsContainer: FC<ComparerResultsContainerProps> = ({
  savedAppA,
  savedAppB,
  savedLabelA,
  savedLabelB
}) => {
  const { metadata } = useMetadata();
  const { showTable } = useDialogs();

  const ensureLatestFormat = (app: Application): Application => migrateApplication(app, metadata, showTable);

  const [results, setResults] = useState<TypeComparerResults>();
  const [selected, setSelected] = useState<ComparerSummaryID>();
  const [appA, setAppA] = useState<Application>(() => ensureLatestFormat(savedAppA));
  const [appB, setAppB] = useState<Application>(() => ensureLatestFormat(savedAppB));
  const [labelA, setLabelA] = useState<string>(savedLabelA);
  const [labelB, setLabelB] = useState<string>(savedLabelB);

  const handleSwap = () => {
    const comparison = compareApplications(appB, appA, metadata);
    setResults(comparison);
    setLabelA(labelB);
    setLabelB(labelA);
    setAppA(appB);
    setAppB(appA);
  };

  useEffect(() => {
    const comparison = compareApplications(appA, appB, metadata);
    setResults(comparison);
  }, []);

  return (
    <Stack height={'calc(100vh - 48px)'} width='100%' justifySelf='center' alignItems='flex-start' pl={1}>
      <ComparerContextProvider>
        <ComparerSummary
          labelA={labelA}
          labelB={labelB}
          results={results}
          selected={selected}
          setSelected={(newSelected) => setSelected(newSelected)}
          handleSwap={handleSwap}
        />
        {selected && <ComparerResults results={results} selected={selected} />}
      </ComparerContextProvider>
    </Stack>
  );
};

export default ComparerResultsContainer;
